import { useEffect, useState } from 'react';
import { api, send } from './api.js';
import { Notice } from './components.js';
import { t } from './i18n.js';

type Template = { id: string; name: string; subject: string; body: string; text: string };
type Signature = { source: string; text: string };

export function TeamMail({ role }: { role: string }) {
  const manager = ['owner', 'admin'].includes(role);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [signature, setSignature] = useState<Signature>({ source: '', text: '' });
  const [draft, setDraft] = useState({ id: '', name: '', subject: '', body: '' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const [items, value] = await Promise.all([
      api<Template[]>('/team-mail/templates'),
      api<Signature>('/team-mail/signature'),
    ]);
    setTemplates(items);
    setSignature(value);
  };
  useEffect(() => {
    void load().catch((e) => setError(e.message));
  }, []);
  const run = async (action: () => Promise<void>, message: string) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      setNotice(message);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="pro-card">
      <h2>{t('Modèles d’équipe et signature d’entreprise')}</h2>
      <p>
        {t(
          'Les modèles sont accessibles à tous les membres. La signature d’entreprise s’ajoute aux nouveaux messages rédigés dans le webmail.',
        )}
      </p>
      {error && <Notice type="error">{error}</Notice>}
      {notice && <Notice>{notice}</Notice>}
      <h3>{t('Modèles disponibles')}</h3>
      {templates.length ? (
        <div className="pro-people">
          {templates.map((item) => (
            <article key={item.id}>
              <strong>{item.name}</strong>
              <span>{item.subject || t('Sans objet prédéfini')}</span>
              <p>{item.text.slice(0, 220)}</p>
              {manager && (
                <div>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({
                        id: item.id,
                        name: item.name,
                        subject: item.subject,
                        body: item.body,
                      })
                    }
                  >
                    {t('Modifier')}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        await send(`/team-mail/templates/${item.id}`, {}, 'DELETE');
                        await load();
                        if (draft.id === item.id)
                          setDraft({ id: '', name: '', subject: '', body: '' });
                      }, t('Modèle supprimé.'))
                    }
                  >
                    {t('Supprimer')}
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      ) : (
        <p>{t('Aucun modèle pour le moment.')}</p>
      )}
      {manager && (
        <>
          <form
            className="pro-form"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await send(
                  `/team-mail/templates${draft.id ? '/' + draft.id : ''}`,
                  { name: draft.name, subject: draft.subject, body: draft.body },
                  draft.id ? 'PUT' : 'POST',
                );
                setDraft({ id: '', name: '', subject: '', body: '' });
                await load();
              }, t('Modèle enregistré.'));
            }}
          >
            <h3>{t(draft.id ? 'Modifier le modèle' : 'Créer un modèle')}</h3>
            <label>
              {t('Nom du modèle')}
              <input
                required
                maxLength={120}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder={t('Réponse commerciale')}
              />
            </label>
            <label>
              {t('Objet proposé')}
              <input
                maxLength={998}
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              />
            </label>
            <label>
              {t('Contenu du message')}
              <textarea
                required
                maxLength={20000}
                rows={6}
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                placeholder={t('Bonjour, …')}
              />
            </label>
            <button disabled={busy}>{t('Enregistrer le modèle')}</button>
            {draft.id && (
              <button
                type="button"
                onClick={() => setDraft({ id: '', name: '', subject: '', body: '' })}
              >
                {t('Annuler')}
              </button>
            )}
          </form>
          <form
            className="pro-form"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await send('/team-mail/signature', { source: signature.source }, 'PUT');
                await load();
              }, t('Signature d’entreprise enregistrée.'));
            }}
          >
            <h3>{t('Signature d’entreprise')}</h3>
            <p>
              {t(
                'Texte simple ou HTML. Le contenu HTML est nettoyé avant son affichage et son envoi.',
              )}
            </p>
            <label>
              {t('Signature')}
              <textarea
                maxLength={20000}
                rows={6}
                value={signature.source}
                onChange={(e) => setSignature({ ...signature, source: e.target.value })}
                placeholder={t('L’équipe Vename Mail')}
              />
            </label>
            <button disabled={busy}>{t('Enregistrer la signature')}</button>
          </form>
        </>
      )}
    </section>
  );
}
